if (!customElements.get('product-bought-together')) {
  class ProductBoughtTogether extends HTMLElement {
    constructor() {
      super();

      this.loading = false;
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onMainVariantChange = this.onMainVariantChange.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.productScope = this.closest('.product-main-content, .product-main');
      this.mainItem = this.querySelector('[data-main-product]');
      this.mainInput = this.querySelector('[data-bought-together-main-input]');
      this.totalElement = this.querySelector('[data-bought-together-total]');
      this.submitButton = this.querySelector('[data-bought-together-submit]');
      this.mainVariants = this.getMainVariants();

      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      document.addEventListener('product:variant-change', this.onMainVariantChange);

      this.querySelectorAll('[data-bought-together-variant-select]').forEach((select) => {
        this.syncRecommendedItem(select);
      });

      this.updateTotal();
      this.updateButtonState();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('product:variant-change', this.onMainVariantChange);
    }

    getMainVariants() {
      const variantsScript = this.querySelector('[data-bought-together-main-variants]');
      if (!variantsScript) return [];

      try {
        const variants = JSON.parse(variantsScript.textContent || '[]');
        return Array.isArray(variants) ? variants : [];
      } catch (error) {
        console.error('Error parsing bought-together variants', error);
        return [];
      }
    }

    formatMoney(cents) {
      return window.Shopify.formatMoney(Number(cents) || 0);
    }

    updateItemImage(item, image120, image240, imageAlt) {
      const image = item?.querySelector('.product-bought-together-image img');
      if (!image || !image240) return;

      image.src = image240;
      image.srcset = [
        image120 && `${image120} 120w`,
        `${image240} 240w`,
      ].filter(Boolean).join(', ');
      image.alt = imageAlt || '';
    }

    syncRecommendedItem(select) {
      const item = select.closest('[data-bought-together-item]');
      const input = item?.querySelector('[data-bought-together-input]');
      const priceElement = item?.querySelector('[data-bought-together-item-price]');
      const selectedOption = select.selectedOptions[0];

      if (!item || !input || !selectedOption) return;

      const available = selectedOption.dataset.available === 'true' && !selectedOption.disabled;
      const price = Number(selectedOption.dataset.price || 0);

      input.value = selectedOption.value;
      input.disabled = !available;
      if (!available) input.checked = false;

      item.dataset.available = String(available);
      item.dataset.price = String(price);

      if (priceElement) priceElement.textContent = this.formatMoney(price);

      this.updateItemImage(
        item,
        selectedOption.getAttribute('data-variant-image-120'),
        selectedOption.getAttribute('data-variant-image-240'),
        selectedOption.getAttribute('data-variant-image-alt')
      );
    }

    syncMainItem(variant) {
      if (!this.mainItem || !this.mainInput) return;

      const priceElement = this.mainItem.querySelector('[data-bought-together-item-price]');
      const variantSelect = this.mainItem.querySelector('[data-bought-together-main-variant-select]');

      if (!variant) {
        this.mainInput.value = '';
        this.mainItem.dataset.available = 'false';
        this.mainItem.dataset.price = '0';
        if (priceElement) priceElement.textContent = this.dataset.unavailableText || '';
        return;
      }

      this.mainInput.value = String(variant.id);
      this.mainItem.dataset.available = String(Boolean(variant.available));
      this.mainItem.dataset.price = String(Number(variant.price || 0));

      if (priceElement) priceElement.textContent = this.formatMoney(variant.price);
      if (variantSelect) variantSelect.value = String(variant.id);

      this.updateItemImage(
        this.mainItem,
        variant.image_120,
        variant.image_240,
        variant.image_alt
      );
    }

    updateTotal() {
      if (!this.totalElement || !this.mainItem) return;

      const mainAvailable = this.mainItem.dataset.available === 'true';
      const mainSelected = Boolean(this.mainInput?.checked);

      if (mainSelected && (!mainAvailable || !this.mainInput?.value)) {
        this.totalElement.textContent = this.dataset.unavailableText || '';
        this.updateButtonState();
        return;
      }

      let total = mainSelected
        ? Number(this.mainItem.dataset.price || 0)
        : 0;

      this.querySelectorAll('[data-bought-together-input]:checked').forEach((input) => {
        if (input.disabled) return;

        const item = input.closest('[data-bought-together-item]');
        total += Number(item?.dataset.price || 0);
      });

      this.totalElement.textContent = this.formatMoney(total);
      this.updateButtonState();
    }

    updateButtonState() {
      if (!this.submitButton) return;

      const mainSelected = Boolean(this.mainInput?.checked);
      const mainAvailable = this.mainItem?.dataset.available === 'true' && Boolean(this.mainInput?.value);
      const selectedComplementaryItems = this.querySelectorAll(
        '[data-bought-together-input]:checked:not(:disabled)'
      ).length;
      const selectedItemCount = (mainSelected && mainAvailable ? 1 : 0) + selectedComplementaryItems;
      const disabled = this.loading || (mainSelected && !mainAvailable) || selectedItemCount === 0;

      this.submitButton.disabled = disabled;
      this.submitButton.setAttribute('aria-disabled', String(disabled));
    }

    onChange(event) {
      const mainVariantSelect = event.target.closest('[data-bought-together-main-variant-select]');

      if (mainVariantSelect && this.contains(mainVariantSelect)) {
        const variant = this.mainVariants.find(
          (item) => String(item.id) === String(mainVariantSelect.value)
        ) || null;

        this.syncMainItem(variant);
        this.updateTotal();
        return;
      }

      const variantSelect = event.target.closest('[data-bought-together-variant-select]');

      if (variantSelect && this.contains(variantSelect)) {
        this.syncRecommendedItem(variantSelect);
        this.updateTotal();
        return;
      }

      const checkbox = event.target.closest('[data-bought-together-input]');
      const mainCheckbox = event.target.closest('[data-bought-together-main-input]');
      if ((checkbox || mainCheckbox) && this.contains(event.target)) this.updateTotal();
    }

    onMainVariantChange(event) {
      const eventSectionId = event.detail?.sectionId;
      if (this.sectionId && eventSectionId && String(this.sectionId) !== String(eventSectionId)) return;

      const variantId = event.detail?.variant?.id;
      const variant = this.mainVariants.find((item) => String(item.id) === String(variantId)) || null;

      this.syncMainItem(variant);
      this.updateTotal();
    }

    buildCartFormData() {
      const formData = new FormData();
      const productForm = this.productScope?.querySelector('product-buy-buttons .product-buttons-form');
      const productFormData = productForm ? new FormData(productForm) : null;
      let itemIndex = 0;

      if (this.mainInput.checked && this.mainItem.dataset.available === 'true' && this.mainInput.value) {
        formData.append(`items[${itemIndex}][id]`, this.mainInput.value);
        formData.append(`items[${itemIndex}][quantity]`, '1');

        if (productFormData) {
          const sellingPlan = productFormData.get('selling_plan');
          if (sellingPlan) formData.append(`items[${itemIndex}][selling_plan]`, sellingPlan);

          productFormData.forEach((value, key) => {
            if (!key.startsWith('properties[')) return;

            const propertyName = key.slice('properties['.length, -1);
            formData.append(`items[${itemIndex}][properties][${propertyName}]`, value);
          });
        }

        itemIndex += 1;
      }

      this.querySelectorAll('[data-bought-together-input]:checked').forEach((input) => {
        if (input.disabled || !input.value) return;

        formData.append(`items[${itemIndex}][id]`, input.value);
        formData.append(`items[${itemIndex}][quantity]`, input.dataset.quantity || '1');
        itemIndex += 1;
      });

      return formData;
    }

    setLoading(loading) {
      this.loading = loading;
      this.toggleAttribute('aria-busy', loading);

      if (this.submitButton) {
        this.submitButton.textContent = loading
          ? this.dataset.addingText || ''
          : this.dataset.addSelectedText || '';
      }

      this.updateButtonState();
    }

    async addSelectedItems() {
      if (this.loading) return;

      const cartComponent = document.querySelector('cart-component');
      if (!cartComponent || typeof cartComponent.add !== 'function') return;

      const formData = this.buildCartFormData();
      const hasSelectedItems = Array.from(formData.keys()).some((key) => key.endsWith('[id]'));
      if (!hasSelectedItems) return;

      this.setLoading(true);

      try {
        await cartComponent.add(formData);
      } finally {
        this.setLoading(false);
      }
    }

    onClick(event) {
      const submitButton = event.target.closest('[data-bought-together-submit]');
      if (!submitButton || !this.contains(submitButton)) return;

      event.preventDefault();
      this.addSelectedItems();
    }
  }

  customElements.define('product-bought-together', ProductBoughtTogether);
}
